import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Image from "@tiptap/extension-image";
import Mention from "@tiptap/extension-mention";
import { Node, mergeAttributes } from "@tiptap/core";

import { StyledCardStack, StyledTypography } from "./styles";

const EntityLinkRender = Node.create({
  name: "entityLink",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes() {
    return {
      entityType: { default: null },
      entityId: { default: null },
      label: { default: null },
      prefix: { default: null },
      route: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-entity-link]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-entity-link": "",
        "data-route": HTMLAttributes.route || "",
        class: `entity-link entity-${HTMLAttributes.entityType || "default"}`,
      }),
      HTMLAttributes.label,
    ];
  },
});

const extensions = [
  StarterKit,
  Underline,
  Link,
  Highlight,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  TextStyle,
  Color,
  Image,
  Mention.configure({
    HTMLAttributes: { class: "comment-mention" },
    renderHTML({ node }) {
      return ["span", { class: "comment-mention" }, `@${node.attrs.label}`];
    },
  }),
  EntityLinkRender,
];

const CommentBody = ({ body }) => {
  const ref = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e) => {
      const link = e.target.closest("[data-route]");
      if (link?.dataset.route) {
        e.preventDefault();
        navigate(link.dataset.route);
      }
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [navigate, body]);

  if (!body) {
    return (
      <Typography
        variant="caption"
        fontSize={12}
        color="text.disabled"
        fontStyle="italic"
      >
        This comment was deleted.
      </Typography>
    );
  }

  const html = generateHTML(body, extensions);

  return (
    <StyledTypography
      ref={ref}
      variant="caption"
      fontSize={12}
      lineHeight="18px"
      component="div"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default CommentBody;
